/**
 * Shared PSF badge renderer.
 *
 * WHY THIS FILE EXISTS
 * ---------------------
 * Previously, `professional-badge-designer.tsx` (single print) and
 * `bulkprint.tsx` (bulk print) each had their OWN copy-pasted drawing code.
 * Over time the two copies drifted apart (different fonts, different QR
 * positioning, different BADGE_HEIGHT), so the same person could get a
 * visibly different-looking badge depending on which screen printed it.
 *
 * On top of that, every vertical position below the photo was computed
 * relative to the one above it (`roleY = nameY + nameHeightUsed + 10`, etc).
 * That meant a long name pushed the role box down, which pushed the green
 * section down, which pushed the QR code down — sometimes off the bottom
 * of the badge entirely. That's exactly the corruption seen in the bug
 * report (long names crushing/cutting off the footer).
 *
 * THE FIX
 * -------
 * 1. One renderer, imported by both single and bulk print. There is no
 *    longer a second copy that can drift.
 * 2. A FIXED layout: every section (photo, name, role, green footer,
 *    company name, QR) has a constant Y position and height, regardless of
 *    how long the name/role/company text is. Nothing downstream ever moves.
 * 3. Text that doesn't fit its box is auto-shrunk (down to a minimum size)
 *    and, if still too long, ellipsis-truncated — it never wraps into an
 *    unbounded number of lines and never grows its container.
 * 4. Photos are cropped "cover" style but anchored slightly above center
 *    (a standard portrait-crop bias) so faces stay framed consistently,
 *    and a fixed-size, always-fits placeholder is used when there's no
 *    photo, instead of text that could overflow the frame.
 * 5. One real, universally-available font family (Arial/Helvetica) is used
 *    everywhere. The previous code referenced a "stencile" font that was
 *    never actually loaded anywhere in the project (no @font-face), so it
 *    silently fell back to whatever default font each browser/printer
 *    happened to pick — a second, hidden source of inconsistency between
 *    single and bulk print.
 */

export const BADGE_WIDTH = 220
export const BADGE_HEIGHT = 311

export const PSF_ORANGE = "#ba4a00"
export const PSF_GREEN = "#0e6655"
export const MIXED_GREEN_BLACK = "#212f3d"

const FONT_FAMILY = "Arial, Helvetica, sans-serif"

export interface BadgeRenderInfo {
  person_full_name: string
  role: string
  exhibitor_name: string
  exhibition_title: string
  stand_category: string
  role_color?: string
}

export interface BadgeRenderData {
  badge_info: BadgeRenderInfo
  photo_url?: string | null
  has_photo: boolean
}

export interface BadgeRenderAssets {
  photoImage: HTMLImageElement | null
  qrImage: HTMLImageElement
  logoImage: HTMLImageElement | null
  showLogo: boolean
}

// ---------------------------------------------------------------------
// FIXED layout constants — nothing below is ever recalculated from text
// length. This is what guarantees every badge looks the same shape.
// ---------------------------------------------------------------------
const HEADER_HEIGHT = 44
const RIGHT_SECTION_HEIGHT = 30
const LOGO_SIZE = 44

const PHOTO_WIDTH = 65
const PHOTO_HEIGHT = 75
const PHOTO_X = (BADGE_WIDTH - PHOTO_WIDTH) / 2
const PHOTO_Y = HEADER_HEIGHT + 15

// Name block: fixed 2-line box directly under the photo. Long names shrink
// font size / truncate to fit here — they never push the layout below.
const NAME_TOP = PHOTO_Y + PHOTO_HEIGHT + 12
const NAME_LINE_HEIGHT = 15
const NAME_MAX_LINES = 2
const NAME_BOX_HEIGHT = NAME_LINE_HEIGHT * NAME_MAX_LINES // 30
const NAME_MAX_WIDTH = PHOTO_WIDTH + 60

// Role pill: fixed position, right after the fixed-height name box.
const ROLE_Y = NAME_TOP + NAME_BOX_HEIGHT + 8
const ROLE_HEIGHT = 35
const ROLE_WIDTH = BADGE_WIDTH * 0.83
const ROLE_X = (BADGE_WIDTH - ROLE_WIDTH) / 2

// Green footer: fixed position, fills the rest of the badge.
const GREEN_Y = ROLE_Y + ROLE_HEIGHT
const GREEN_WIDTH = BADGE_WIDTH - 18.5

// Company name: fixed 1-line box at the top of the green section.
const COMPANY_Y = GREEN_Y + 15
const COMPANY_MAX_WIDTH = BADGE_WIDTH * 0.86

// QR / logo: fixed distance from the BOTTOM of the badge, so it never
// collides with the company name above it, no matter how the rest of the
// layout is configured.
const QR_SIZE = 44
const QR_BOTTOM_MARGIN = 10
const QR_Y = BADGE_HEIGHT - QR_SIZE - QR_BOTTOM_MARGIN

function drawRoundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
) {
  ctx.beginPath()
  ctx.moveTo(x + radius, y)
  ctx.lineTo(x + width - radius, y)
  ctx.quadraticCurveTo(x + width, y, x + width, y + radius)
  ctx.lineTo(x + width, y + height - radius)
  ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height)
  ctx.lineTo(x + radius, y + height)
  ctx.quadraticCurveTo(x, y + height, x, y + height - radius)
  ctx.lineTo(x, y + radius)
  ctx.quadraticCurveTo(x, y, x + radius, y)
  ctx.closePath()
}

function drawRoundedRectTopOnly(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
) {
  ctx.beginPath()
  ctx.moveTo(x + radius, y)
  ctx.lineTo(x + width - radius, y)
  ctx.quadraticCurveTo(x + width, y, x + width, y + radius)
  ctx.lineTo(x + width, y + height)
  ctx.lineTo(x, y + height)
  ctx.lineTo(x, y + radius)
  ctx.quadraticCurveTo(x, y, x + radius, y)
  ctx.closePath()
}

/**
 * Fits `text` into a box of `maxWidth` x (maxLines * lineHeight) by shrinking
 * the font size (down to minFontSize) and, if it still doesn't fit,
 * ellipsis-truncating the final line. Returns the lines to draw and the
 * font size that was actually used, so the box height is GUARANTEED never
 * to exceed maxLines * lineHeight — callers never need to re-measure or
 * shift anything below this block.
 */
function fitTextToBox(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  maxLines: number,
  lineHeight: number,
  startFontSize: number,
  minFontSize: number,
  weight: string = "bold",
): { lines: string[]; fontSize: number } {
  const words = (text || "").trim().split(/\s+/).filter(Boolean)
  if (words.length === 0) return { lines: [""], fontSize: startFontSize }

  const wrap = (fontSize: number): string[] => {
    ctx.font = `${weight} ${fontSize}px ${FONT_FAMILY}`
    const lines: string[] = []
    let line = ""
    for (const word of words) {
      const testLine = line ? `${line} ${word}` : word
      if (ctx.measureText(testLine).width > maxWidth && line) {
        lines.push(line)
        line = word
      } else {
        line = testLine
      }
    }
    if (line) lines.push(line)
    return lines
  }

  let fontSize = startFontSize
  let lines = wrap(fontSize)

  // Shrink font until it fits within maxLines, down to the floor.
  while (lines.length > maxLines && fontSize > minFontSize) {
    fontSize -= 1
    lines = wrap(fontSize)
  }

  // Still doesn't fit at the minimum size: hard-truncate with ellipsis.
  if (lines.length > maxLines) {
    ctx.font = `${weight} ${fontSize}px ${FONT_FAMILY}`
    const kept = lines.slice(0, maxLines)
    let lastLine = kept[maxLines - 1]
    // Append any overflow words to the last line before truncating so we
    // don't just silently drop them without any indication.
    const usedWords = kept.join(" ").split(/\s+/).length
    const overflowWords = words.slice(usedWords)
    if (overflowWords.length > 0) {
      lastLine = `${lastLine} ${overflowWords.join(" ")}`
    }
    while (lastLine.length > 1 && ctx.measureText(`${lastLine}…`).width > maxWidth) {
      lastLine = lastLine.slice(0, -1)
    }
    kept[maxLines - 1] = `${lastLine}…`
    lines = kept
  }

  ctx.font = `${weight} ${fontSize}px ${FONT_FAMILY}`
  return { lines, fontSize }
}

/** Draws lines centered horizontally at `centerX`, vertically centered within a box of height `boxHeight` starting at `boxTop`. */
function drawCenteredLines(
  ctx: CanvasRenderingContext2D,
  lines: string[],
  centerX: number,
  boxTop: number,
  boxHeight: number,
  lineHeight: number,
) {
  const blockHeight = lines.length * lineHeight
  const startY = boxTop + (boxHeight - blockHeight) / 2 + lineHeight * 0.78
  ctx.textAlign = "center"
  lines.forEach((line, i) => {
    ctx.fillText(line, centerX, startY + i * lineHeight)
  })
}

/**
 * Draws a photo "cover"-fit into the given box, anchored slightly above
 * center (25% from the top of the overflow instead of 50/50) so that
 * headshots keep the subject's face in frame consistently, regardless of
 * each source photo's own aspect ratio or where the subject sits in it.
 */
function drawPhotoCover(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  boxX: number,
  boxY: number,
  boxW: number,
  boxH: number,
) {
  const scale = Math.max(boxW / img.width, boxH / img.height)
  const scaledW = img.width * scale
  const scaledH = img.height * scale
  const drawX = boxX - (scaledW - boxW) / 2
  const verticalBias = 0.25 // 0 = top-anchored, 0.5 = center, 1 = bottom-anchored
  const drawY = boxY - (scaledH - boxH) * verticalBias
  ctx.drawImage(img, drawX, drawY, scaledW, scaledH)
}

/**
 * Renders one PSF badge onto `ctx` at 1x badge units (caller is responsible
 * for setting up canvas size + DPI scale before calling this). This is the
 * ONLY place badge layout is defined — both single-print and bulk-print
 * call this function so they can never visually diverge again.
 */
export function renderBadge(
  ctx: CanvasRenderingContext2D,
  data: BadgeRenderData,
  assets: BadgeRenderAssets,
) {
  const { badge_info } = data
  const { photoImage, qrImage, logoImage, showLogo } = assets

  ctx.clearRect(0, 0, BADGE_WIDTH, BADGE_HEIGHT)
  ctx.textBaseline = "alphabetic"

  // 1. Background
  ctx.fillStyle = MIXED_GREEN_BLACK
  ctx.fillRect(0, 0, BADGE_WIDTH, BADGE_HEIGHT)

  // 2. Header: black left half + white right half (curved bottom-left corner)
  ctx.fillStyle = MIXED_GREEN_BLACK
  ctx.fillRect(LOGO_SIZE + 2, 0, BADGE_WIDTH / 2 - (LOGO_SIZE + 2), HEADER_HEIGHT)

  const xRight = BADGE_WIDTH / 2 + 4
  const rightWidth = BADGE_WIDTH / 2 - 4
  const cornerRadius = 10
  ctx.fillStyle = "#ffffff"
  ctx.beginPath()
  ctx.moveTo(xRight, 0)
  ctx.lineTo(xRight + rightWidth, 0)
  ctx.lineTo(xRight + rightWidth, RIGHT_SECTION_HEIGHT)
  ctx.lineTo(xRight + cornerRadius, RIGHT_SECTION_HEIGHT)
  ctx.arcTo(xRight, RIGHT_SECTION_HEIGHT, xRight, RIGHT_SECTION_HEIGHT - cornerRadius, cornerRadius)
  ctx.lineTo(xRight, 0)
  ctx.closePath()
  ctx.fill()

  // 3. Logo (top-left) + exhibition title
  if (logoImage) {
    ctx.drawImage(logoImage, 0, HEADER_HEIGHT - LOGO_SIZE, LOGO_SIZE * 0.8, LOGO_SIZE)
  }
  // Title fits on 1 line when short (Y stays ≈ 18, matching the old layout).
  // When long, it wraps to 2 lines with a reduced font — both lines are
  // vertically centred inside the white right section (RIGHT_SECTION_HEIGHT = 30).
  //
  // WHY TITLE_X uses xRight+4 instead of the old LOGO_SIZE+90 (=134):
  //   The white panel starts at xRight (=114). The old offset wasted 20 px of
  //   white space before the text even began, leaving only 80 px of usable width.
  //   Anchoring 4 px inside the white panel gives 96 px — enough for a long title
  //   like "PSF-EASTERN PROVINCE EXPO 2026" to wrap to 2 lines at a small font
  //   instead of being hard-truncated.
  const TITLE_LINE_HEIGHT = 11
  const TITLE_RIGHT_X = BADGE_WIDTH - 6           // 6 px right-pad; text anchor for right-align
  const TITLE_MAX_WIDTH = TITLE_RIGHT_X - (xRight + 4)  // usable width: right-pad to 4 px inside panel
  const { fontSize: titleFontSize, lines: titleLines } = fitTextToBox(
    ctx,
    (badge_info.exhibition_title || "").toUpperCase(),
    TITLE_MAX_WIDTH,
    2,           // allow up to 2 lines for long titles
    12,
    12,
    6,           // shrink down to 6 px to get a balanced 2-line split
  )
  ctx.fillStyle = "#000000"
  ctx.font = `bold ${titleFontSize}px ${FONT_FAMILY}`
  // Right-align so text sits flush against the right edge of the white panel,
  // giving natural breathing room from the left boundary.
  ctx.textAlign = "right"
  const titleBlockHeight = titleLines.length * TITLE_LINE_HEIGHT
  const titleStartY = (RIGHT_SECTION_HEIGHT - titleBlockHeight) / 2 + TITLE_LINE_HEIGHT * 0.78
  titleLines.forEach((line, i) => {
    ctx.fillText(line, TITLE_RIGHT_X, titleStartY + i * TITLE_LINE_HEIGHT)
  })

  // 4. Photo area background block
  ctx.fillStyle = MIXED_GREEN_BLACK
  ctx.fillRect(0, HEADER_HEIGHT, BADGE_WIDTH / 2 + 40, 100)

  // Photo — soft shadow border
  ctx.save()
  ctx.shadowColor = "rgba(254, 250, 250, 0.1)"
  ctx.shadowBlur = 5
  ctx.shadowOffsetY = 2
  drawRoundedRect(ctx, PHOTO_X - 4, PHOTO_Y - 4, PHOTO_WIDTH + 8, PHOTO_HEIGHT + 8, 15)
  ctx.fillStyle = "rgb(254, 250, 250)"
  ctx.fill()
  ctx.restore()

  // Photo itself, clipped to a fixed rounded box — always centered
  // horizontally at BADGE_WIDTH/2 regardless of any other content.
  ctx.save()
  drawRoundedRect(ctx, PHOTO_X, PHOTO_Y, PHOTO_WIDTH, PHOTO_HEIGHT, 12)
  ctx.fillStyle = "#f8f9fa"
  ctx.fill()
  ctx.clip()
  if (photoImage) {
    drawPhotoCover(ctx, photoImage, PHOTO_X, PHOTO_Y, PHOTO_WIDTH, PHOTO_HEIGHT)
  } else {
    // Fixed-size placeholder that is guaranteed to fit inside the box —
    // no more text spilling past the clip region.
    ctx.fillStyle = "#9aa1a8"
    ctx.beginPath()
    ctx.arc(PHOTO_X + PHOTO_WIDTH / 2, PHOTO_Y + PHOTO_HEIGHT * 0.4, PHOTO_WIDTH * 0.22, 0, Math.PI * 2)
    ctx.fill()
    ctx.beginPath()
    ctx.ellipse(
      PHOTO_X + PHOTO_WIDTH / 2,
      PHOTO_Y + PHOTO_HEIGHT * 0.95,
      PHOTO_WIDTH * 0.34,
      PHOTO_HEIGHT * 0.32,
      0,
      Math.PI,
      2 * Math.PI,
    )
    ctx.fill()
  }
  ctx.restore()

  // 5. Person name — fixed 2-line box, auto-shrunk/truncated to fit.
  const { lines: nameLines, fontSize: nameFontSize } = fitTextToBox(
    ctx,
    badge_info.person_full_name || "",
    NAME_MAX_WIDTH,
    NAME_MAX_LINES,
    NAME_LINE_HEIGHT,
    14,
    10,
  )
  ctx.fillStyle = "#ffffff"
  ctx.font = `bold ${nameFontSize}px ${FONT_FAMILY}`
  drawCenteredLines(ctx, nameLines, BADGE_WIDTH / 2, NAME_TOP, NAME_BOX_HEIGHT, NAME_LINE_HEIGHT)

  // 6. Role pill — fixed position/size, text auto-shrunk to one line.
  const displayRole = badge_info.role || badge_info.stand_category || ""
  const roleColor = badge_info.role_color || PSF_ORANGE
  ctx.save()
  drawRoundedRectTopOnly(ctx, ROLE_X, ROLE_Y, ROLE_WIDTH, ROLE_HEIGHT, 12)
  ctx.fillStyle = roleColor
  ctx.fill()
  ctx.restore()

  const { lines: roleLines, fontSize: roleFontSize } = fitTextToBox(
    ctx,
    displayRole,
    ROLE_WIDTH - 12,
    1,
    18,
    16,
    10,
  )
  ctx.fillStyle = "#ffffff"
  ctx.font = `bold ${roleFontSize}px ${FONT_FAMILY}`
  ctx.textAlign = "center"
  ctx.fillText(roleLines[0] || "", ROLE_X + ROLE_WIDTH / 2, ROLE_Y + ROLE_HEIGHT / 2 + roleFontSize * 0.35)

  // Fill the tiny quarter-circle gap so white never peeks through.
  ctx.save()
  ctx.fillStyle = roleColor
  ctx.fillRect(ROLE_X + ROLE_WIDTH - 12, ROLE_Y + ROLE_HEIGHT, 12, 12)
  ctx.restore()

  // 7. Green footer — fixed position, always fills the rest of the badge.
  const greenSectionHeight = BADGE_HEIGHT - GREEN_Y
  ctx.save()
  ctx.fillStyle = PSF_GREEN
  ctx.beginPath()
  ctx.moveTo(0, GREEN_Y + greenSectionHeight)
  ctx.lineTo(0, GREEN_Y)
  ctx.lineTo(GREEN_WIDTH - 12, GREEN_Y)
  ctx.arcTo(GREEN_WIDTH, GREEN_Y, GREEN_WIDTH, GREEN_Y + 12, 12)
  ctx.lineTo(GREEN_WIDTH, GREEN_Y + greenSectionHeight)
  ctx.lineTo(0, GREEN_Y + greenSectionHeight)
  ctx.closePath()
  ctx.fill()
  ctx.restore()

  // 8. Company (exhibitor) name — fixed 1-line box at top of green section.
  const { lines: companyLines, fontSize: companyFontSize } = fitTextToBox(
    ctx,
    badge_info.exhibitor_name || "",
    COMPANY_MAX_WIDTH,
    1,
    12,
    12,
    8,
  )
  ctx.fillStyle = "#ffffff"
  ctx.font = `bold ${companyFontSize}px ${FONT_FAMILY}`
  ctx.textAlign = "center"
  ctx.fillText(companyLines[0] || "", BADGE_WIDTH / 2, COMPANY_Y)

  // 9. QR (+ optional logo) — fixed distance from the badge's bottom edge.
  //    Never depends on how tall anything above it turned out to be.
  if (showLogo && logoImage) {
    const logoW = LOGO_SIZE * 0.8
    const logoX = 21
    const logoY = QR_Y + (QR_SIZE - LOGO_SIZE) / 2
    ctx.fillStyle = "#ffffff"
    ctx.fillRect(logoX - 2, logoY - 2, logoW + 4, LOGO_SIZE + 3)
    ctx.drawImage(logoImage, logoX, logoY, logoW, LOGO_SIZE)

    const qrX = BADGE_WIDTH - QR_SIZE - 23
    ctx.save()
    ctx.shadowColor = PSF_GREEN
    ctx.shadowBlur = 6
    ctx.shadowOffsetX = 2
    ctx.shadowOffsetY = 3
    drawRoundedRect(ctx, qrX - 2, QR_Y - 2, QR_SIZE + 4, QR_SIZE + 4, 6)
    ctx.fillStyle = PSF_GREEN
    ctx.fill()
    ctx.restore()

    ctx.save()
    drawRoundedRect(ctx, qrX, QR_Y, QR_SIZE, QR_SIZE, 4)
    ctx.clip()
    ctx.drawImage(qrImage, qrX, QR_Y, QR_SIZE, QR_SIZE)
    ctx.restore()
  } else {
    const qrX = (BADGE_WIDTH - QR_SIZE) / 2
    ctx.save()
    ctx.shadowColor = PSF_GREEN
    ctx.shadowBlur = 6
    ctx.shadowOffsetX = 2
    ctx.shadowOffsetY = 3
    drawRoundedRect(ctx, qrX - 2, QR_Y - 2, QR_SIZE + 4, QR_SIZE + 4, 6)
    ctx.fillStyle = PSF_GREEN
    ctx.fill()
    ctx.restore()

    ctx.save()
    drawRoundedRect(ctx, qrX, QR_Y, QR_SIZE, QR_SIZE, 4)
    ctx.clip()
    ctx.drawImage(qrImage, qrX, QR_Y, QR_SIZE, QR_SIZE)
    ctx.restore()
  }
}

/** Sets up a canvas at the correct high-DPI badge size and returns its 2D context, ready for `renderBadge`. */
export function prepareBadgeCanvas(canvas: HTMLCanvasElement, dpiScale: number): CanvasRenderingContext2D {
  canvas.width = BADGE_WIDTH * dpiScale
  canvas.height = BADGE_HEIGHT * dpiScale
  const ctx = canvas.getContext("2d")
  if (!ctx) throw new Error("Could not get canvas 2D context")
  ctx.setTransform(1, 0, 0, 1, 0, 0) // reset in case of reuse
  ctx.scale(dpiScale, dpiScale)
  ctx.imageSmoothingEnabled = true
  ctx.imageSmoothingQuality = "high"
  return ctx
}

// ---------------------------------------------------------------------
// Shared bulk-print SHEET layout — used by both the front-badge bulk
// printer (bulkprint.tsx) and the back-of-badge sheet printer
// (back-badge-print-modal.tsx). Both read the SAME constants here so
// that when an operator flips a printed front sheet over and feeds the
// back sheet through the printer, the back design lands in exactly the
// same physical position as the front badge it belongs behind.
// ---------------------------------------------------------------------
export const SHEET_GRID_COLS = 2
export const SHEET_GRID_ROWS = 2
export const SHEET_BADGES_PER_PAGE = SHEET_GRID_COLS * SHEET_GRID_ROWS
export const SHEET_GUTTER_UNITS = 12 // gap between adjacent cells (~0.22in at DPI_SCALE 8)
export const SHEET_OUTER_MARGIN_UNITS = 8 // gap around the whole sheet (~0.15in at DPI_SCALE 8)
export const SHEET_DPI_SCALE = 8
export const A4_PAGE_SIZE_IN = { width: 8.27, height: 11.69 }

/** Loads an <img> from a URL/data-URL as a Promise, with a timeout. Shared by front bulk print and back-of-badge sheet printing. */
export function loadImageAsync(src: string, timeoutMs = 10000): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = "anonymous"
    const timeout = setTimeout(() => {
      reject(new Error(`Image load timeout: ${src}`))
    }, timeoutMs)
    img.onload = () => {
      clearTimeout(timeout)
      resolve(img)
    }
    img.onerror = () => {
      clearTimeout(timeout)
      reject(new Error(`Failed to load image: ${src}`))
    }
    img.src = src
  })
}

/**
 * Faint dash-dot cutting guides + a scissors glyph at the gutter crossing
 * point of a gridCols x gridRows sheet — the same visual used for front
 * badge sheets, so front and back sheets are cut identically.
 */
export function drawSheetCutGuides(
  ctx: CanvasRenderingContext2D,
  pageWidth: number,
  pageHeight: number,
  cellW: number,
  cellH: number,
  gutter: number,
  margin: number,
  dpiScale: number,
  gridCols: number = SHEET_GRID_COLS,
  gridRows: number = SHEET_GRID_ROWS,
) {
  const lineWidth = Math.max(1, dpiScale * 0.25)
  const dash = dpiScale * 1.2
  const gap = dpiScale * 1
  const dot = Math.max(1, dpiScale * 0.25)

  ctx.save()
  ctx.strokeStyle = "#b3b3b3"
  ctx.lineWidth = lineWidth
  ctx.lineCap = "round"
  ctx.setLineDash([dash, gap, dot, gap])

  if (gridCols > 1) {
    const vx = margin + cellW + gutter / 2
    ctx.beginPath()
    ctx.moveTo(vx, 0)
    ctx.lineTo(vx, pageHeight)
    ctx.stroke()
  }
  if (gridRows > 1) {
    const hy = margin + cellH + gutter / 2
    ctx.beginPath()
    ctx.moveTo(0, hy)
    ctx.lineTo(pageWidth, hy)
    ctx.stroke()
  }

  ctx.setLineDash([])

  if (gridCols > 1 && gridRows > 1) {
    const cx = margin + cellW + gutter / 2
    const cy = margin + cellH + gutter / 2
    const fontSize = Math.max(8, Math.min(gutter, dpiScale * 3.5))
    ctx.font = `${fontSize}px sans-serif`
    ctx.fillStyle = "#999999"
    ctx.textAlign = "center"
    ctx.textBaseline = "middle"
    ctx.fillText("✂", cx, cy)
  }
  ctx.restore()
}

/**
 * Draws `img` "contain"-fit (never cropped, never distorted) centered
 * within a cell of cellW x cellH, on a white backdrop. Used for the back
 * sheet, where the source is a sponsor/info page — cropping a sponsor
 * logo the way a headshot gets cropped would be unacceptable, so this
 * always shows the whole image, letterboxed if the aspect ratio doesn't
 * exactly match the badge card.
 */
export function drawImageContain(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  cellX: number,
  cellY: number,
  cellW: number,
  cellH: number,
) {
  ctx.fillStyle = "#ffffff"
  ctx.fillRect(cellX, cellY, cellW, cellH)
  const scale = Math.min(cellW / img.width, cellH / img.height)
  const drawW = img.width * scale
  const drawH = img.height * scale
  const drawX = cellX + (cellW - drawW) / 2
  const drawY = cellY + (cellH - drawH) / 2
  ctx.drawImage(img, drawX, drawY, drawW, drawH)
}

/**
 * Composes a gridCols x gridRows sheet from up to gridCols*gridRows
 * already-loaded images, tiled with the same gutter/margin geometry used
 * for front badge sheets. Each cell is "contain"-fit (see drawImageContain)
 * rather than stretched, so a back-design image doesn't get distorted.
 * If fewer images than cells are given, the last image is reused to fill
 * the remaining cells — used for the back-of-badge sheet, where every
 * cell shows the identical sponsor design.
 */
export function composeGridSheet(
  images: HTMLImageElement[],
  cellWidthUnits: number,
  cellHeightUnits: number,
  dpiScale: number = SHEET_DPI_SCALE,
  gridCols: number = SHEET_GRID_COLS,
  gridRows: number = SHEET_GRID_ROWS,
  gutterUnits: number = SHEET_GUTTER_UNITS,
  outerMarginUnits: number = SHEET_OUTER_MARGIN_UNITS,
): string {
  const cellW = cellWidthUnits * dpiScale
  const cellH = cellHeightUnits * dpiScale
  const gutter = gutterUnits * dpiScale
  const margin = outerMarginUnits * dpiScale

  const pageCanvas = document.createElement("canvas")
  pageCanvas.width = cellW * gridCols + gutter * (gridCols - 1) + margin * 2
  pageCanvas.height = cellH * gridRows + gutter * (gridRows - 1) + margin * 2

  const ctx = pageCanvas.getContext("2d")
  if (!ctx) throw new Error("Could not get page canvas context")

  ctx.fillStyle = "#ffffff"
  ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height)

  const totalCells = gridCols * gridRows
  for (let i = 0; i < totalCells; i++) {
    const img = images[i] || images[images.length - 1]
    if (!img) continue
    const col = i % gridCols
    const row = Math.floor(i / gridCols)
    const x = margin + col * (cellW + gutter)
    const y = margin + row * (cellH + gutter)
    drawImageContain(ctx, img, x, y, cellW, cellH)
  }

  drawSheetCutGuides(ctx, pageCanvas.width, pageCanvas.height, cellW, cellH, gutter, margin, dpiScale, gridCols, gridRows)

  return pageCanvas.toDataURL("image/png", 1.0)
}